/* eslint-disable */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Globe, Plus, Building2, Loader2, Trash2, ChevronRight,
  Users, FlaskConical, MapPin, Clock, Search, Briefcase, Flag
} from 'lucide-react';
import { toast } from 'sonner';
import { getAccountStatusColor } from '@/lib/statusColors';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

const API = '/api';

const STATUS_COLORS = {
  'Active': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'On Hold': 'bg-amber-50 text-amber-700 border-amber-200',
  'Closed - Won': 'bg-blue-50 text-blue-700 border-blue-200',
  'Closed - Lost': 'bg-slate-100 text-slate-500 border-slate-200',
};

const TYPE_LABEL = {
  pharma: 'Pharma',
  regional_distributor: 'Distributor',
};

const EMPTY_FORM = {
  project_name: '', client_company: '', client_crm_id: '',
  client_name: '', client_email: '', client_phone: '',
  client_location: '', client_type: 'pharma',
};

export default function AccountsPage() {
  const [projects, setProjects] = useState([]);
  const [typeCounts, setTypeCounts] = useState({ pharma: 0, regional_distributor: 0 });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState('');
  const [showDealLines, setShowDealLines] = useState(false); // retained no-op (deal lines removed)
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') || 'accounts';

  // The page is mounted at two routes — `/accounts` (default = pharma) and
  // `/regional-distributors` (forces regional_distributor filter).
  const isDistributorRoute = location.pathname.startsWith('/regional-distributors');
  const activeType = isDistributorRoute ? 'regional_distributor' : 'pharma';

  const [introductions, setIntroductions] = useState([]);
  const [introLoading, setIntroLoading] = useState(false);

  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [searchingClient, setSearchingClient] = useState(false);
  const [clientFocused, setClientFocused] = useState(false);
  const clientTimeout = useRef(null);

  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [searchingContact, setSearchingContact] = useState(false);
  // Only show the contact dropdown / "no-match" hint while the input has
  // focus. Otherwise the hint sticks around and overlaps adjacent fields
  // (Email/Phone) once the user has moved on. Tracked on focus/blur with a
  // small blur delay so click-to-select still fires.
  const [contactFocused, setContactFocused] = useState(false);
  const contactTimeout = useRef(null);

  // When the user opens the create dialog on /regional-distributors,
  // pre-select that type so they don't have to switch every time.
  useEffect(() => {
    setForm(f => ({ ...f, client_type: activeType }));
  }, [activeType]);

  const fetchProjects = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/india-licensing?client_type=${activeType}`,
        { withCredentials: true },
      );
      setProjects(data.projects || []);
      setTypeCounts(data.type_counts || { pharma: 0, regional_distributor: 0 });
    } catch { toast.error('Failed to load accounts'); }
    finally { setLoading(false); }
  }, [activeType]);

  useEffect(() => { setLoading(true); fetchProjects(); }, [fetchProjects]);

  useEffect(() => {
    if (view === 'introductions') {
      setIntroLoading(true);
      axios.get(`${API}/india-licensing/introductions`, { withCredentials: true })
        .then(res => setIntroductions(res.data || []))
        .catch(() => {})
        .finally(() => setIntroLoading(false));
    }
  }, [view]);

  const searchClient = useCallback(async (q) => {
    if (!q || q.length < 2) { setClientResults([]); return; }
    setSearchingClient(true);
    try {
      const { data } = await axios.get(`${API}/india-licensing/search/crm-companies?q=${encodeURIComponent(q)}`, { withCredentials: true });
      setClientResults(data.results || []);
    } catch { setClientResults([]); }
    finally { setSearchingClient(false); }
  }, []);

  const handleClientSearchChange = (val) => {
    setClientSearch(val);
    setForm(f => ({ ...f, client_company: val }));
    clearTimeout(clientTimeout.current);
    clientTimeout.current = setTimeout(() => searchClient(val), 300);
  };

  const selectClient = (r) => {
    setForm(f => ({
      ...f,
      client_company: r.company,
      client_location: r.country || f.client_location,
      client_name: '', client_email: '', client_phone: '',
    }));
    setClientSearch(r.company);
    setClientResults([]);
    setContactSearch('');
    setContactResults([]);
  };

  const searchContacts = useCallback(async (q) => {
    const company = form.client_company;
    if (!company || company.length < 2) { setContactResults([]); return; }
    setSearchingContact(true);
    try {
      const { data } = await axios.get(`${API}/india-licensing/search/contacts-by-company?company=${encodeURIComponent(company)}&q=${encodeURIComponent(q || '')}`, { withCredentials: true });
      setContactResults(data.results || []);
    } catch { setContactResults([]); }
    finally { setSearchingContact(false); }
  }, [form.client_company]);

  const handleContactSearchChange = (val) => {
    setContactSearch(val);
    setForm(f => ({ ...f, client_name: val }));
    clearTimeout(contactTimeout.current);
    contactTimeout.current = setTimeout(() => searchContacts(val), 300);
  };

  const selectContact = (r) => {
    setForm(f => ({ ...f, client_name: r.name, client_email: r.email || f.client_email, client_phone: r.phone || f.client_phone }));
    setContactSearch(r.name);
    setContactResults([]);
  };

  const handleCreate = async () => {
    if (!form.project_name.trim() || !form.client_company.trim()) {
      toast.error('Account name and client company are required');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/india-licensing`, form, { withCredentials: true });
      console.log('[create-account] OK', res?.data);
      toast.success('Account created');
      setShowCreate(false);
      setForm({ ...EMPTY_FORM });
      setClientSearch('');
      fetchProjects();
    } catch (err) {
      console.error('[create-account] FAILED', err?.response?.status, err?.response?.data, err);
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      toast.error(`Failed to create account: ${detail}`, { duration: 8000 });
    }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/india-licensing/${deleteId}`, { withCredentials: true });
      toast.success('Account deleted');
      setDeleteId(null);
      fetchProjects();
    } catch (err) {
      console.error('[delete-account] FAILED', err?.response?.status, err?.response?.data, err);
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      toast.error(`Failed to delete: ${detail}`, { duration: 8000 });
    }
  };

  const filtered = projects.filter(p => {
    if (!search) return true;
    const s = search.toLowerCase();
    return p.project_name?.toLowerCase().includes(s) ||
      p.client_company?.toLowerCase().includes(s) ||
      p.client_name?.toLowerCase().includes(s);
  });

  const activeCount = projects.filter(p => p.status === 'Active').length;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="accounts-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-6" data-testid="accounts-page">
      {/* Header — title absolute-left with descriptor, toolbar wraps onto a
          second row on narrow viewports to keep the heading column clean.
          Mirrors the PharmaContactDetailPage header pattern. */}
      <div className="mb-2">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-1">
          <h1
            className="text-3xl font-bold tracking-tight text-slate-900 whitespace-nowrap"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="accounts-page-title"
          >
            {isDistributorRoute ? 'Distributors' : 'Accounts'}
          </h1>
          <p className="text-sm text-slate-500">
            {isDistributorRoute
              ? 'Distributors and non-pharma in-licensers (Tamer Group, Lunatus, etc.)'
              : 'Pharma accounts — track biotech responses and active deals.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {/* Quick toggle to swap views */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-md p-1" data-testid="accounts-type-toggle">
            <button
              onClick={() => navigate('/accounts')}
              className={`px-3 py-1 text-[11px] font-medium rounded ${!isDistributorRoute ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="accounts-toggle-pharma"
            >
              Pharma <span className="text-slate-400 ml-1">({typeCounts.pharma})</span>
            </button>
            <button
              onClick={() => navigate('/regional-distributors')}
              className={`px-3 py-1 text-[11px] font-medium rounded ${isDistributorRoute ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="accounts-toggle-distributor"
            >
              Distributor <span className="text-slate-400 ml-1">({typeCounts.regional_distributor})</span>
            </button>
          </div>
          <Button onClick={() => { setShowCreate(true); setClientSearch(''); setContactSearch(''); setContactResults([]); }} className="bg-[#0A1128] text-white hover:bg-slate-800 h-9 px-4 text-sm" data-testid="create-account-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New {isDistributorRoute ? 'Partner' : 'Account'}
          </Button>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex items-center gap-1 border-b border-slate-100">
        {[
          { key: 'accounts', label: 'All Accounts', count: projects.length },
          { key: 'introductions', label: 'Introductions Made', count: introductions.length || '...' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setSearchParams(tab.key === 'accounts' ? {} : { view: tab.key })}
            className={`px-4 py-2 text-xs font-semibold transition-colors border-b-2 ${view === tab.key ? 'border-[#00709B] text-[#00709B]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
            data-testid={`accounts-tab-${tab.key}`}>
            {tab.label} <span className="ml-1.5 text-[10px] font-normal">({tab.count})</span>
          </button>
        ))}
      </div>

      {view === 'introductions' ? (
        /* Introductions View */
        introLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : introductions.length === 0 ? (
          <Card className="border-dashed border-slate-200">
            <CardContent className="py-12 text-center">
              <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No introductions made yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="border border-slate-100 rounded-lg overflow-hidden">
            <div className="grid grid-cols-[1fr_1fr_0.8fr_0.6fr_0.8fr] px-4 py-2 bg-slate-50 border-b border-slate-100">
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Biotech Company</span>
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Account (Pharma)</span>
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Status</span>
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">CDA</span>
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Introduced</span>
            </div>
            <div className="divide-y divide-slate-50 max-h-[600px] overflow-y-auto">
              {introductions.map((r, i) => (
                <div key={r.response_id || i} className="grid grid-cols-[1fr_1fr_0.8fr_0.6fr_0.8fr] px-4 py-3 items-center hover:bg-slate-50/50 cursor-pointer"
                  onClick={() => navigate(`/accounts/${r.project_id}`)} data-testid={`intro-row-${i}`}>
                  <div>
                    <p className="text-xs font-medium text-slate-900">{r.biotech_company_name}</p>
                  </div>
                  <p className="text-xs text-slate-600">{r.account_name || r.account_company}</p>
                  <Badge variant="outline" className={`text-[10px] w-fit ${getAccountStatusColor(r.status || 'Introduced')}`}>{r.status || 'Introduced'}</Badge>
                  <p className="text-xs text-slate-500">{r.bilateral_cda_status || '—'}</p>
                  <p className="text-xs text-slate-400">{r.introduced_at ? new Date(r.introduced_at).toLocaleDateString() : '—'}</p>
                </div>
              ))}
            </div>
          </div>
        )
      ) : (
      /* Accounts View */
      <>
      <div className="grid grid-cols-2 gap-4">
        {[
          { label: 'Total Accounts', value: projects.length, icon: Briefcase, color: 'text-[#00709B]', bg: 'bg-[#00709B]/10' },
          { label: 'Active', value: activeCount, icon: Building2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
        ].map((s, i) => (
          <Card
            key={i}
            className="border-slate-100"
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center shrink-0`}>
                <s.icon className={`w-5 h-5 ${s.color}`} />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.value}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Search accounts, companies, contacts..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 text-sm" data-testid="search-accounts-input" />
      </div>

      {/* Lined up for matching — manual flag set on the account page (mirrors Dashboard "Lined up for next outreach") */}
      {(() => {
        const lined = filtered.filter(p => p.next_matching?.flagged);
        if (!lined.length) return null;
        return (
          <Card className="border border-emerald-200 bg-emerald-50/30 shadow-sm" data-testid="next-matching-strip">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <Flag className="w-4 h-4 fill-emerald-500 text-emerald-600" />
                <span className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Lined up for matching</span>
                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">{lined.length}</Badge>
                <span className="text-[11px] text-slate-500">· run these accounts' matching next; clears automatically when you open Send Update</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                {lined.map(p => (
                  <div key={`nm-${p.project_id}`} onClick={() => navigate(`/accounts/${p.project_id}`)}
                    className="group flex items-start gap-2 p-2.5 rounded-lg border border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors cursor-pointer"
                    data-testid={`next-matching-item-${p.project_id}`}>
                    <Flag className="w-3.5 h-3.5 fill-emerald-500 text-emerald-600 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900 truncate">{p.client_company}{p.client_name ? <span className="font-normal text-slate-500"> · {p.client_name}</span> : null}</p>
                      {p.next_matching?.note && <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{p.next_matching.note}</p>}
                      <p className="text-[10px] text-slate-400 mt-0.5">Flagged {p.next_matching?.flagged_at ? new Date(p.next_matching.flagged_at).toLocaleDateString() : '—'}{p.last_matching_run_at ? ` · last run ${new Date(p.last_matching_run_at).toLocaleDateString()}` : ' · never run'}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-600 shrink-0 mt-0.5" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {filtered.length === 0 ? (
        <Card className="border-dashed border-slate-200">
          <CardContent className="py-12 text-center">
            <Briefcase className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No accounts yet. Create your first account.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(p => {
            const tas = p.client_therapeutic_areas || [];
            const stages = p.client_preferred_stages || [];
            return (
              <Card key={p.project_id} className="border-slate-100 hover:border-[#00709B]/30 hover:shadow-sm transition-all cursor-pointer group"
                onClick={() => navigate(`/accounts/${p.project_id}`)} data-testid={`account-card-${p.project_id}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#00709B]/10 flex items-center justify-center shrink-0">
                    <Briefcase className="w-5 h-5 text-[#00709B]" />
                  </div>
                  <div className="flex-1 min-w-0 max-w-[40%]">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-slate-900 truncate">{p.project_name}</span>
                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${STATUS_COLORS[p.status] || ''}`}>{p.status}</Badge>
                      {p.next_matching?.flagged && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-emerald-50 text-emerald-700 border-emerald-200 flex items-center gap-1" title={p.next_matching.note || 'Run matching next'} data-testid={`next-matching-badge-${p.project_id}`}>
                          <Flag className="w-2.5 h-2.5 fill-emerald-500" /> Matching next
                        </Badge>
                      )}
                      {p.client_type === 'regional_distributor' && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200" title="Non-pharma in-licenser / commercialization partner">
                          {TYPE_LABEL.regional_distributor}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {p.client_company}</span>
                      {p.client_name && (
                        p.client_pharma_contact_id ? (
                          <a
                            href={`/pharma/${p.client_pharma_contact_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1 hover:text-[#00709B] hover:underline"
                            title={`Open ${p.client_name}'s CRM profile in a new tab`}
                            data-testid={`account-contact-link-${p.project_id}`}
                          >
                            <Users className="w-3 h-3" /> {p.client_name}
                          </a>
                        ) : (
                          <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {p.client_name}</span>
                        )
                      )}
                    </div>
                  </div>
                  {/* Therapeutic areas column — left half of the priorities band */}
                  <div className="hidden md:flex flex-1 min-w-0 justify-end" data-testid={`account-priorities-${p.project_id}`}>
                    {tas.length > 0 && (
                      <div className="flex flex-wrap justify-end gap-1 max-w-[300px]" title={`Therapeutic interests: ${tas.join(', ')}`}>
                        {tas.slice(0, 4).map((t) => (
                          <Badge key={`ta-${t}`} variant="outline" className="text-[10px] px-1.5 py-0 bg-sky-50 text-sky-700 border-sky-200" data-testid={`account-ta-${t}`}>{t}</Badge>
                        ))}
                        {tas.length > 4 && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-slate-50 text-slate-500 border-slate-200" title={tas.slice(4).join(', ')}>+{tas.length - 4}</Badge>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Vertical divider — only when both sides have content */}
                  {tas.length > 0 && stages.length > 0 && (
                    <div className="hidden md:block w-px h-8 bg-slate-200 shrink-0" />
                  )}
                  {/* Stages column — modality chips removed on user request
                      (2026-07-21): synonym-heavy purple chips cluttered the
                      list. Modalities stay on the contact profile. */}
                  <div className="hidden md:flex flex-1 min-w-0 justify-start flex-wrap gap-x-3 gap-y-1">
                    {stages.length > 0 && (
                      <div className="flex flex-wrap justify-start gap-1" title={`Preferred stages: ${stages.join(', ')}`}>
                        {stages.slice(0, 4).map((s) => (
                          <Badge key={`stg-${s}`} variant="outline" className="text-[10px] px-1.5 py-0 bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`account-stage-${s}`}>{s}</Badge>
                        ))}
                        {stages.length > 4 && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-slate-50 text-slate-500 border-slate-200" title={stages.slice(4).join(', ')}>+{stages.length - 4}</Badge>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setDeleteId(p.project_id); }}
                    className="p-1.5 rounded text-slate-300 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0 opacity-0 group-hover:opacity-100"
                    title="Delete account"
                    data-testid={`delete-account-btn-${p.project_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#00709B] transition-colors shrink-0" />
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      </>
      )}

      {/* Create Account Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg" data-testid="create-account-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>New Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-medium text-slate-600">Account Name *</Label>
              <Input value={form.project_name} onChange={e => setForm(f => ({ ...f, project_name: e.target.value }))}
                placeholder="e.g., Akums In-licensing" className="mt-1 h-9 text-sm" data-testid="input-account-name" />
            </div>
            <div className="relative">
              <Label className="text-xs font-medium text-slate-600">Client Company * <span className="text-slate-400 font-normal">(search CRM)</span></Label>
              <div className="relative mt-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input value={clientSearch || form.client_company} onChange={e => handleClientSearchChange(e.target.value)}
                  onFocus={() => setClientFocused(true)}
                  onBlur={() => setTimeout(() => setClientFocused(false), 150)}
                  placeholder="e.g., client company name" className="pl-8 h-9 text-sm" data-testid="input-client-company" />
                {searchingClient && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-slate-400" />}
              </div>
              {clientFocused && clientResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {clientResults.map((r, i) => (
                    <button key={i} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm"
                      onClick={() => selectClient(r)} data-testid={`client-result-${i}`}>
                      <Building2 className="w-3.5 h-3.5 text-[#00709B] shrink-0" />
                      <span className="font-medium truncate">{r.company}</span>
                      {r.country && <span className="text-[10px] text-slate-400 ml-auto">{r.country}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <Label className="text-xs font-medium text-slate-600">Contact Person <span className="text-slate-400 font-normal">(search)</span></Label>
                <Input value={contactSearch || form.client_name} onChange={e => handleContactSearchChange(e.target.value)}
                  onFocus={() => setContactFocused(true)}
                  onBlur={() => setTimeout(() => setContactFocused(false), 150)}
                  placeholder="e.g., Vaibhav" className="mt-1 h-9 text-sm" data-testid="input-client-name" />
                {contactFocused && contactResults.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {contactResults.map((r, i) => (
                      <button key={i} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
                        onClick={() => selectContact(r)} data-testid={`contact-result-${i}`}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{r.name}</span>
                          {r.email && <span className="text-slate-400 text-xs ml-auto">{r.email}</span>}
                        </div>
                        {r.note && <div className="text-[10px] text-amber-600 italic mt-0.5">{r.note}</div>}
                      </button>
                    ))}
                  </div>
                )}
                {contactFocused && contactSearch && contactSearch.length >= 2 && !searchingContact && contactResults.length === 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg">
                    <div className="px-3 py-2 text-xs text-slate-500">
                      No match in CRM. Typing this name will save it as a new freeform contact.
                    </div>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Location</Label>
                <Input value={form.client_location} onChange={e => setForm(f => ({ ...f, client_location: e.target.value }))}
                  className="mt-1 h-9 text-sm" data-testid="input-client-location" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-600">Email</Label>
                <Input value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))}
                  className="mt-1 h-9 text-sm" data-testid="input-client-email" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Phone</Label>
                <Input value={form.client_phone} onChange={e => setForm(f => ({ ...f, client_phone: e.target.value }))}
                  className="mt-1 h-9 text-sm" data-testid="input-client-phone" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Account Type</Label>
              <div className="mt-1 flex items-center gap-1.5">
                {[
                  { v: 'pharma',               label: 'Pharma' },
                  { v: 'regional_distributor', label: 'Distributor' },
                ].map(opt => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, client_type: opt.v }))}
                    className={
                      form.client_type === opt.v
                        ? 'px-3 py-1.5 text-xs font-medium rounded-md border bg-[#00709B] text-white border-[#00709B]'
                        : 'px-3 py-1.5 text-xs font-medium rounded-md border bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }
                    data-testid={`form-client-type-${opt.v}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="h-9 text-sm">Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#0A1128] text-white hover:bg-slate-800 h-9 text-sm" data-testid="submit-create-account">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} Create Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Account?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete this account and its history.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-account">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
